import { onMounted, onUnmounted, ref, computed, watch, Ref } from 'vue';
import { Api } from '@/Api';

export interface ThumbnailPreview {
  timestamp: number;
  url: string;
}

export interface UseChooseFrameOptions {
  itemId: string;
  contentUrl: string;
  currentTimestamp?: number | null;
}

// Kept low enough that each filmstrip tile stays close to the source's 16:9
// ratio at the modal's width — more tiles means narrower boxes, and
// object-cover crops harder the further a tile's box strays from that ratio,
// which can crop content (e.g. on-screen text) out of view entirely.
export const FRAME_COUNT = 10;
const HIGH_RES_WIDTH = 640;
const HIGH_RES_HEIGHT = 360;
const HIGH_RES_DEBOUNCE_MS = 200;
const VIDEO_METADATA_TIMEOUT_MS = 10_000;

// Evenly-spaced integer timestamps across the video, deduplicated (a very
// short video can otherwise produce repeated rounded values). Sampling stops
// one second short of the reported duration — the container's reported
// duration is sometimes a hair past the last decodable frame, and requesting
// a preview at (or beyond) that point renders as a blank/white image.
const buildTimestamps = (duration: number): number[] => {
  if (!Number.isFinite(duration) || duration <= 0) {
    return [];
  }

  const maxTimestamp = Math.max(0, Math.floor(duration) - 1);
  const count = Math.min(FRAME_COUNT, maxTimestamp + 1);
  if (count <= 1) {
    return [0];
  }

  const step = maxTimestamp / (count - 1);
  const values = Array.from({ length: count }, (_, i) => Math.round(i * step));

  return Array.from(new Set(values));
};

// item.attributes.info.duration is only populated once backend metadata
// extraction has run, which isn't guaranteed for every video — so read the
// authoritative duration straight from the browser's own video metadata
// instead of trusting a possibly-absent server-computed field.
const probeDuration = (src: string): Promise<number> =>
  new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const cleanup = () => {
      clearTimeout(timeoutId);
      video.onloadedmetadata = null;
      video.onerror = null;
      video.removeAttribute('src');
    };
    const timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error('Timed out loading video metadata'));
    }, VIDEO_METADATA_TIMEOUT_MS);

    video.preload = 'metadata';
    video.muted = true;
    video.onloadedmetadata = () => {
      const { duration } = video;
      cleanup();
      resolve(duration);
    };
    video.onerror = () => {
      cleanup();
      reject(new Error('Failed to load video metadata'));
    };
    video.src = src;
  });

export const useChooseFrame = (options: UseChooseFrameOptions) => {
  const { itemId, contentUrl, currentTimestamp } = options;

  const previews = ref<ThumbnailPreview[]>([]) as Ref<ThumbnailPreview[]>;
  const isLoading = ref(false);
  const hasError = ref(false);
  const selectedTimestamp = ref<number | null>(null);
  // Keyed by timestamp so re-selecting a frame doesn't refetch it.
  const highResUrls = ref<Record<number, string>>({});

  const selectedPreview = computed(() => {
    const base = previews.value.find(preview => preview.timestamp === selectedTimestamp.value) ?? previews.value[0] ?? null;
    if (!base) {
      return null;
    }
    const highResUrl = highResUrls.value[base.timestamp];
    return highResUrl ? { ...base, url: highResUrl } : base;
  });

  const selectFrame = (timestamp: number) => {
    if (previews.value.length === 0) {
      return;
    }

    const nearest = previews.value.reduce((closest, preview) =>
      Math.abs(preview.timestamp - timestamp) < Math.abs(closest.timestamp - timestamp) ? preview : closest
    );

    selectedTimestamp.value = nearest.timestamp;
  };

  const fetchHighResPreview = async (timestamp: number) => {
    if (highResUrls.value[timestamp]) {
      return;
    }
    try {
      const [preview] = await Api.item.thumbnailPreviews(itemId, [timestamp], { width: HIGH_RES_WIDTH, height: HIGH_RES_HEIGHT });
      if (preview) {
        highResUrls.value = { ...highResUrls.value, [timestamp]: preview.url };
      }
    } catch {
      // Large preview just stays on the filmstrip-resolution fallback.
    }
  };

  // Debounced so dragging across many tiles doesn't fire a high-res request
  // per frame — only the timestamp the user settles on gets fetched.
  let highResTimer: ReturnType<typeof setTimeout> | null = null;
  let isUnmounted = false;
  watch(selectedTimestamp, timestamp => {
    if (highResTimer) {
      clearTimeout(highResTimer);
    }
    if (timestamp == null) {
      return;
    }
    highResTimer = setTimeout(() => fetchHighResPreview(timestamp), HIGH_RES_DEBOUNCE_MS);
  });

  onUnmounted(() => {
    isUnmounted = true;
    if (highResTimer) {
      clearTimeout(highResTimer);
    }
  });

  const load = async () => {
    if (!contentUrl) {
      hasError.value = true;
      return;
    }

    isLoading.value = true;
    hasError.value = false;
    try {
      const duration = await probeDuration(contentUrl);
      if (isUnmounted) {
        return;
      }
      const timestamps = buildTimestamps(duration);
      if (timestamps.length === 0) {
        hasError.value = true;
        return;
      }

      const fetched = await Api.item.thumbnailPreviews(itemId, timestamps);
      if (isUnmounted) {
        return;
      }
      previews.value = fetched ?? [];

      if (previews.value.length === 0) {
        hasError.value = true;
        return;
      }

      const firstTimestamp = previews.value[0].timestamp;
      const lastTimestamp = previews.value[previews.value.length - 1].timestamp;
      const currentInRange = currentTimestamp != null && currentTimestamp >= firstTimestamp && currentTimestamp <= lastTimestamp;
      if (currentInRange) {
        selectFrame(currentTimestamp as number);
      } else {
        selectedTimestamp.value = firstTimestamp;
      }
    } catch {
      hasError.value = true;
    } finally {
      isLoading.value = false;
    }
  };

  onMounted(load);

  return {
    previews,
    isLoading,
    hasError,
    selectedTimestamp,
    selectedPreview,
    selectFrame,
    load,
  };
};
